import { filterTruthy } from "@redotech/util/array";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocalStorage } from "usehooks-ts";

const SEARCH_HISTORY_KEY = "redo-command-palette-search-history";
const MAX_HISTORY_ITEMS = 5;

export function useSearchHistory() {
  const [rawSearchHistory, setRawSearchHistory] = useLocalStorage<string[]>(
    SEARCH_HISTORY_KEY,
    [],
  );

  // Track if we need to reset the history due to invalid data
  const needsResetRef = useRef(false);

  const searchHistory = useMemo(() => {
    if (
      Array.isArray(rawSearchHistory) &&
      rawSearchHistory.every((item) => typeof item === "string")
    ) {
      needsResetRef.current = false;
      return filterTruthy(rawSearchHistory);
    }
    // Mark for reset, but don't call setState during render
    needsResetRef.current = true;
    return [];
  }, [rawSearchHistory]);

  // Reset invalid data in an effect, not during render
  useEffect(() => {
    if (needsResetRef.current) {
      setRawSearchHistory([]);
      needsResetRef.current = false;
    }
  }, [searchHistory, setRawSearchHistory]);
  const appendToSearchHistory = useCallback(
    (newItem: string) => {
      if (!newItem.trim()) {
        return;
      }
      const ordered = new Set([newItem.trim(), ...searchHistory]);
      const sliced = Array.from(ordered).slice(0, MAX_HISTORY_ITEMS);
      setRawSearchHistory(sliced);
    },
    [searchHistory, setRawSearchHistory],
  );

  return { searchHistory, saveToSearchHistory: appendToSearchHistory };
}
